import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { usePagedList } from '../hooks/usePagedList';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';

interface Hospital {
  id: string; code: string; name: string;
  city: string | null; email: string | null; phone: string | null;
  isActive: boolean;
}

const EMPTY = { code: '', name: '', city: '', province: '', email: '', phone: '' };

export function Hospitals() {
  const { can } = useAuth();
  const list = usePagedList<Hospital>('/hospitals');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function openForm() {
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  }

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await api.post('/hospitals', {
        code: form.code, name: form.name,
        city: form.city || null, province: form.province || null,
        email: form.email || null, phone: form.phone || null,
      });
      setOpen(false);
      setNotice(`Rumah sakit ${form.code} ditambahkan. Buat user-nya di halaman Pengguna.`);
      list.refresh();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Gagal menyimpan rumah sakit.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Rumah Sakit</h2>
        <p>Rumah sakit dibuat lebih dulu, lalu user-nya menggantung padanya.</p>
      </div>

      {notice && <div className="msg ok">{notice}</div>}

      <DataTable
        list={list}
        searchPlaceholder="Cari kode, nama, atau kota…"
        action={can('hospital:write') ? { label: 'Daftarkan rumah sakit', onClick: openForm } : undefined}
        empty="Belum ada rumah sakit yang bisa Anda lihat."
        emptyFiltered="Tidak ada rumah sakit yang cocok dengan pencarian."
        columns={['Kode', 'Nama', 'Kota', 'Email', 'Status']}
      >
        {list.items.map((r) => (
          <tr key={r.id}>
            <td className="mono">{r.code}</td>
            <td>{r.name}</td>
            <td className="muted">{r.city ?? '—'}</td>
            <td className="mono muted">{r.email ?? '—'}</td>
            <td>
              <span className={r.isActive ? 'pill accent' : 'pill'}>
                {r.isActive ? 'aktif' : 'nonaktif'}
              </span>
            </td>
          </tr>
        ))}
      </DataTable>

      <Modal
        open={open}
        title="Daftarkan rumah sakit"
        description="Pesanan darah menggantung pada rumah sakit, bukan pada cabang — kodenya ikut ke nomor pesanan."
        onClose={() => setOpen(false)}
        onSubmit={create}
        footer={
          <>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Batal</button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Simpan rumah sakit'}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: '0.9rem' }}>
          {formError && <div className="msg error">{formError}</div>}
          <div className="grid-2">
            <div className="field">
              <label className="label" htmlFor="h-code">Kode</label>
              <input id="h-code" value={form.code} required placeholder="RSHS"
                onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="h-name">Nama</label>
              <input id="h-name" value={form.name} required placeholder="RSUP Dr. Hasan Sadikin"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="h-city">Kota</label>
              <input id="h-city" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="h-province">Provinsi</label>
              <input id="h-province" value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="h-email">Email</label>
              <input id="h-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="field">
              <label className="label" htmlFor="h-phone">Telepon</label>
              <input id="h-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
